/**
 * LocationAuditService - Manages location verification audit trail
 * Stores and retrieves location verification logs for compliance
 */

import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit as fsLimit,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';

// 'unverified' = check-in recorded but there was no reference location to compare
// against (the appointment/client had no GPS coordinates), so it's neither
// verified nor flagged — just logged for the trail.
export type LocationAuditStatus = 'verified' | 'flagged' | 'approved' | 'rejected' | 'unverified';

export interface LocationAuditLog {
  id: string;
  companyId?: string;
  sessionNoteId: string;
  appointmentId: string;
  rbtId: string;
  rbtName: string;

  // Location data
  checkInLatitude: number;
  checkInLongitude: number;
  appointmentLatitude: number;
  appointmentLongitude: number;
  appointmentAddress: string;

  // Verification result
  distance: number; // miles
  isVerified: boolean;
  requiresApproval: boolean;
  status: LocationAuditStatus;

  // Approval/Rejection
  reviewedBy?: string;
  reviewedAt?: Timestamp;
  reviewNotes?: string;

  // Timestamps
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface CreateLocationAuditLogData {
  companyId: string;
  sessionNoteId: string;
  appointmentId: string;
  rbtId: string;
  rbtName: string;
  checkInLatitude: number;
  checkInLongitude: number;
  appointmentLatitude: number;
  appointmentLongitude: number;
  appointmentAddress: string;
  distance: number;
  isVerified: boolean;
  requiresApproval: boolean;
  status: LocationAuditStatus;
}

class LocationAuditService {
  private collectionName = 'locationAuditLogs';

  /**
   * Create a new location audit log entry
   */
  async createAuditLog(data: CreateLocationAuditLogData): Promise<string> {
    const logRef = doc(collection(db, this.collectionName));
    const logId = logRef.id;

    const now = Timestamp.now();

    const auditLog: Omit<LocationAuditLog, 'id'> = {
      companyId: data.companyId,
      sessionNoteId: data.sessionNoteId,
      appointmentId: data.appointmentId,
      rbtId: data.rbtId,
      rbtName: data.rbtName,
      checkInLatitude: data.checkInLatitude,
      checkInLongitude: data.checkInLongitude,
      appointmentLatitude: data.appointmentLatitude,
      appointmentLongitude: data.appointmentLongitude,
      appointmentAddress: data.appointmentAddress,
      distance: data.distance,
      isVerified: data.isVerified,
      requiresApproval: data.requiresApproval,
      status: data.status,
      createdAt: now,
      updatedAt: now,
    };

    await setDoc(logRef, auditLog);
    console.log(`Created location audit log ${logId} for session note ${data.sessionNoteId}`);
    return logId;
  }

  /**
   * Approve a flagged location
   */
  async approveLocation(
    logId: string,
    reviewedBy: string,
    reviewNotes?: string
  ): Promise<void> {
    const logRef = doc(db, this.collectionName, logId);

    await updateDoc(logRef, {
      status: 'approved',
      reviewedBy,
      reviewedAt: Timestamp.now(),
      reviewNotes: reviewNotes || '',
      updatedAt: Timestamp.now(),
    });

    console.log(`Approved location audit log ${logId}`);
  }

  /**
   * Reject a flagged location
   */
  async rejectLocation(
    logId: string,
    reviewedBy: string,
    reviewNotes?: string
  ): Promise<void> {
    const logRef = doc(db, this.collectionName, logId);

    await updateDoc(logRef, {
      status: 'rejected',
      reviewedBy,
      reviewedAt: Timestamp.now(),
      reviewNotes: reviewNotes || '',
      updatedAt: Timestamp.now(),
    });

    console.log(`Rejected location audit log ${logId}`);
  }

  /**
   * Get audit log by ID
   */
  async getAuditLog(logId: string): Promise<LocationAuditLog | null> {
    const logRef = doc(db, this.collectionName, logId);
    const logSnap = await getDoc(logRef);

    if (!logSnap.exists()) {
      return null;
    }

    return { id: logSnap.id, ...logSnap.data() } as LocationAuditLog;
  }

  /**
   * Get audit log for a session note
   */
  async getAuditLogBySessionNote(sessionNoteId: string): Promise<LocationAuditLog | null> {
    const q = query(
      collection(db, this.collectionName),
      where('sessionNoteId', '==', sessionNoteId)
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    return { id: doc.id, ...doc.data() } as LocationAuditLog;
  }

  /**
   * Get all flagged locations requiring review
   */
  async getFlaggedLocations(): Promise<LocationAuditLog[]> {
    const q = query(
      collection(db, this.collectionName),
      where('status', '==', 'flagged'),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as LocationAuditLog));
  }

  /**
   * Get all audit logs for a company (supervisor/admin view — every check-in,
   * not just flagged ones). Ordered newest first.
   */
  async getAuditLogsByCompany(companyId: string, max: number = 500): Promise<LocationAuditLog[]> {
    const q = query(
      collection(db, this.collectionName),
      where('companyId', '==', companyId),
      orderBy('createdAt', 'desc'),
      fsLimit(max)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as LocationAuditLog));
  }

  /**
   * Get audit logs for an RBT
   */
  async getAuditLogsByRBT(rbtId: string): Promise<LocationAuditLog[]> {
    const q = query(
      collection(db, this.collectionName),
      where('rbtId', '==', rbtId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as LocationAuditLog));
  }

  /**
   * Get audit logs for an appointment
   */
  async getAuditLogsByAppointment(appointmentId: string): Promise<LocationAuditLog[]> {
    const q = query(
      collection(db, this.collectionName),
      where('appointmentId', '==', appointmentId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as LocationAuditLog));
  }

  /**
   * Get statistics for location verification
   */
  async getVerificationStatistics(rbtId?: string): Promise<{
    total: number;
    verified: number;
    flagged: number;
    approved: number;
    rejected: number;
    verificationRate: number;
  }> {
    let q;
    if (rbtId) {
      q = query(
        collection(db, this.collectionName),
        where('rbtId', '==', rbtId)
      );
    } else {
      q = query(collection(db, this.collectionName));
    }

    const snapshot = await getDocs(q);
    const logs = snapshot.docs.map(doc => doc.data() as LocationAuditLog);

    const total = logs.length;
    const verified = logs.filter(log => log.status === 'verified').length;
    const flagged = logs.filter(log => log.status === 'flagged').length;
    const approved = logs.filter(log => log.status === 'approved').length;
    const rejected = logs.filter(log => log.status === 'rejected').length;

    const verificationRate = total > 0 ? (verified / total) * 100 : 0;

    return {
      total,
      verified,
      flagged,
      approved,
      rejected,
      verificationRate: Math.round(verificationRate * 10) / 10, // Round to 1 decimal
    };
  }

  /**
   * Format distance for display
   */
  formatDistance(miles: number): string {
    if (miles < 0.1) {
      const feet = Math.round(miles * 5280);
      return `${feet} ft`;
    }
    return `${miles.toFixed(2)} mi`;
  }

  /**
   * Get status badge color
   */
  getStatusColor(status: LocationAuditStatus): string {
    switch (status) {
      case 'verified':
        return 'bg-green-100 text-green-800';
      case 'flagged':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-blue-100 text-blue-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }
}

export const locationAuditService = new LocationAuditService();
